<script lang="ts">
  /**
   * Waypoint — where we are going next.
   *
   * v0 is the trip book: what we have done, what we are planning, and what we said
   * we would do one day. The spec is blunt that a planner you have to remember to
   * visit is dead by October — so the ideas list exists to be turned into dated
   * plans, and dated plans exist to reach the calendar.
   *
   * Sign-in, the API client and the shell are verbatim copies of Vault's, per the
   * programme convention — one sign-in works across every portal precisely because
   * every client asks the same project who you are.
   */
  import { Shell, Icon, type PortalLink , SignIn } from "@steyncd/steyn-design";
  import { api } from "./lib/api";
  import Trips from "./routes/Trips.svelte";
  import Trip from "./routes/Trip.svelte";
  import Shortlist from "./routes/Shortlist.svelte";
  import { session } from "./lib/stores.svelte";
  import { glance } from "./lib/glance.svelte";
  import { net } from "./lib/offline.svelte";
  import { signIn, signOut, atLeast, completeRedirect, explainAuthError } from "./lib/auth";

  const FRONT_DOOR = import.meta.env.VITE_FRONTDOOR_URL ?? "https://www.helloeben.co.za";

  type Section = "trips" | "reach";
  let section = $state<Section>("trips");

  /**
   * The open trip, held beside the section rather than inside it.
   *
   * A trip is an object page reached from two places — the book and the
   * shortlist — and both must leave you somewhere sensible when you come back.
   * Keeping the id here means "close the trip" returns to whichever list you came
   * from, without either list having to know the other exists.
   */
  let openTripId = $state<string | null>(null);
  function open(id: string) {
    openTripId = id;
  }
  function goto(id: Section) {
    openTripId = null;
    section = id;
  }

  const ICON: Record<string, string> = {
    ha: "house", hq: "finance", screening: "film", codebots: "bot",
    vault: "vault", homestead: "homestead", waypoint: "waypoint",
    hindsight: "hindsight", fabric: "fabric", frontdoor: "door",
  };

  async function loadPortals(): Promise<PortalLink[]> {
    const res = await api.get<{ portals: { id: string; name: string; url: string }[] }>(
      `${import.meta.env.VITE_FABRIC_API_URL}/portals`,
    );
    return res.portals
      .filter((p) => p.id !== "waypoint")
      .map((p) => ({ id: p.id, name: p.name, url: p.url, icon: ICON[p.id] ?? "door" }));
  }

  let signingIn = $state(false);
  let signInError = $state<string | null>(null);

  /**
   * Finish a redirect sign-in before deciding what to render. Without this the
   * credential in the URL is never collected and the gate simply reappears.
   */
  let settling = $state(true);
  $effect(() => {
    void completeRedirect()
      .then((r) => {
        // A redirect that came back with a real error used to be swallowed, so the gate
        // just reappeared with nothing saying why. Surface it like any other failure.
        if (r.error) signInError = explainAuthError(r.error);
      })
      .finally(() => (settling = false));
  });

  async function doSignIn() {
    signingIn = true;
    signInError = null;
    try {
      await signIn();
    } catch (e) {
      const code = (e as { code?: string }).code ?? "";
      if (!/popup-closed-by-user|cancelled-popup-request/.test(code)) {
        // The code is shown, not swallowed. A bare "sign-in failed" has cost this
        // programme several debugging rounds; the code is the whole diagnosis.
        signInError = explainAuthError(e);
      }
    } finally {
      signingIn = false;
    }
  }
</script>

{#if !session.ready || settling}
  <div class="boot" aria-busy="true"></div>
{:else if !session.user}
  <SignIn
    product="Waypoint"
    mark="waypoint"
    tagline="Weekends away that are already costed."
    onsignin={doSignIn}
    busy={signingIn}
    error={signInError}
    glance={glance.stats}
    siblings={["Home", "Finance", "Screening Room", "Vault", "Hindsight", "Codebots"]}
  />
{:else if !atLeast(session.claims?.role ?? null, "partner")}
  <div class="gate">
    <div class="card--hero gate__card">
      <h1>Waypoint</h1>
      <p class="sub">
        {#if session.claims}
          This portal needs a partner account. You are signed in as {session.claims.role}.
        {:else}
          This Google account has no access to the Steyn household.
        {/if}
      </p>
      <a class="btn-primary gate__btn" href={FRONT_DOOR}>Go to the Front Door</a>
      <button class="gate__link" onclick={signOut}>Sign out</button>
    </div>
  </div>
{:else}
  <!-- Waypoint is a home surface: open, serif, 52px rows. A household reads this six or
       eight times a year, not an operator working it daily. -->
  <Shell
    product="Waypoint"
    mark="waypoint"
    surface="home"
    portals={loadPortals}
    nav={[
      { id: "trips", label: "Trips", icon: "trip" },
      { id: "reach", label: "Within reach", icon: "car" },
    ]}
    active={section}
    onnavigate={(id) => goto(id as Section)}
    frontDoorUrl={FRONT_DOOR}
  >
    {#snippet header()}
      {#if !net.online}
        <!-- Stated in the chrome, not only on the screen that noticed: everything
             read from here on is what this device already had. Glyph and word. -->
        <span class="offline"><Icon name="pause" size={13} /> No signal</span>
      {/if}
      <span class="who">
        <Icon name="user" size={14} />
        {session.user?.displayName ?? session.user?.email}
      </span>
      <button class="signout" onclick={signOut} aria-label="Sign out">
        <Icon name="logout" size={16} />
      </button>
    {/snippet}

    {#if openTripId}
      <Trip tripId={openTripId} onback={() => (openTripId = null)} />
    {:else if section === "reach"}
      <Shortlist onopen={open} />
    {:else}
      <Trips onopen={open} />
    {/if}
  </Shell>
{/if}

<style>
  .boot { min-height: 100vh; background: var(--aurora); }
  .gate {
    min-height: 100vh;
    background: var(--aurora);
    display: grid;
    place-items: center;
    padding: 24px;
  }
  .gate__card { width: min(380px, 100%); padding: 30px 28px; text-align: center; }
  h1 { margin: 0 0 6px; font-size: 22px; font-weight: 800; letter-spacing: -0.5px; color: var(--tx); }
  .gate__btn { display: inline-block; margin-top: 18px; padding: 9px 18px; font-size: 12.5px; }
  .gate__link { display: block; margin: 12px auto 0; font-size: 11.5px; font-weight: 700; color: var(--mut); }
  .who { display: inline-flex; align-items: center; gap: 6px; font-size: 11.5px; font-weight: 700; color: var(--mut); }
  .offline {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 2px 8px;
    border: 1px solid var(--warn);
    border-radius: var(--r-pill);
    font-size: var(--fs-micro);
    font-weight: 700;
    color: var(--warn);
  }
  .signout { padding: 6px; border-radius: var(--r-control); color: var(--mut); }
  .signout:hover { color: var(--acc); }
</style>
